// src/pages/Processing.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import { OrderListSkeleton } from "../components/Skeleton";
import "./Processing.css";
import { useLocation } from "react-router-dom";

const Processing = () => {
  const { buyer } = useBuyer();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const serviceType = location.state?.serviceType || null;

  const config = useMemo(() => ({
    statuses: ["processing"],
    title: "Processing Orders",
    showCancel: false,
  }), []);

  useEffect(() => {
    const fetchOrders = async () => {
      const buyerId = buyer?.buyerId;

      if (!buyerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const ordersRef = collection(db, "orderList");
        const constraints = [
          where("buyerId", "==", buyerId),
          where("orderStatus", "==", "Processing")
        ];

        if (serviceType) {
          constraints.push(where("serviceType", "==", serviceType));
        }

        const q = query(ordersRef, ...constraints);
        const snapshot = await getDocs(q);

        const fetchedOrders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setOrders(fetchedOrders);
      } catch (err) {
        console.error("Error fetching processing orders:", err);
        setError(`Failed to load processing orders: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [buyer?.buyerId, serviceType]);

  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return "N/A";
    let date;
    if (typeof dateValue === 'object' && dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else {
      date = new Date(dateValue);
    }
    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  }, []);

  if (loading) {
    return (
      <div className="orders-container">
        <Header title={config.title} />
        <OrderListSkeleton count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container">
        <Header title={config.title} />
        <div className="error-state">
          <p className="error">{error}</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header title={config.title} />
      <div className="orders-container">
        <div className="orders-header">
          <p>{orders.length} order{orders.length !== 1 ? "s" : ""} found</p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <p>No processing orders found.</p>
            <p className="debug-info">
              Buyer ID: {buyer?.buyerId || "Not found"} |
              Statuses: Processing |
              Service Type: {serviceType || "All"}
            </p>
          </div>
        ) : (
          <div className="processing-orders-container">
            {orders.map((order) => {
              const imageUrl = order.images?.[0]?.url?.trim();

              return (
                <div key={order.id} className="order-card">
                  <div className="order-image">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={order.productName || "Product"}
                        loading="lazy"
                      />
                    ) : (
                      <div className="image-placeholder">No Image</div>
                    )}
                  </div>

                  <div className="order-info">
                    <span className="order-specification">
                      {order.category || "-"} / {order.productName || "-"}
                    </span>

                    <p className="order-name">
                      {order.specification || "-"} / {order.styleType || "-"}
                    </p>

                    <span className="order-service-type">
                      {order.serviceType ? order.serviceType.toUpperCase() : "-"}
                    </span>

                    {Array.isArray(order.selectedVariants) && order.selectedVariants.length > 0 && (
                      <div className="variant-display">
                        {order.selectedVariants.map((variant, index) => (
                          <span key={`${variant.size}-${index}`} className="variant-info">
                            {variant.size} x {variant.quantity}
                            {index < order.selectedVariants.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                    )}

                    <p className="order-weight">
                      Qty: {order.quantity || 0}
                    </p>

                    <p className="order-date">
                      Placed: {formatDate(order.orderTime)}
                    </p>

                    <div className={`status-badge status-processing`}>
                      <span>{order.orderStatus || "Processing"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Processing;
